import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { useImageUpload } from '@/hooks/useImageUpload';
import { ImagePlus, X, Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const MAX_ATTACHMENTS = 3;

interface AttachmentUploaderProps {
  attachments: string[];
  onAttachmentsChange: (urls: string[]) => void;
  disabled?: boolean;
}

export function AttachmentUploader({
  attachments,
  onAttachmentsChange,
  disabled = false,
}: AttachmentUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadImage, uploading, error, moderationError, clearModerationError } = useImageUpload();
  const [uploadingCount, setUploadingCount] = useState(0);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const remainingSlots = MAX_ATTACHMENTS - attachments.length;
    const filesToUpload = Array.from(files).slice(0, remainingSlots);

    setUploadingCount(filesToUpload.length);
    clearModerationError();

    const newUrls: string[] = [];

    for (const file of filesToUpload) {
      const result = await uploadImage(file, 'feedback', 'feedback');
      if (result?.url) {
        newUrls.push(result.url);
      }
    }

    if (newUrls.length > 0) {
      onAttachmentsChange([...attachments, ...newUrls]);
    }

    setUploadingCount(0);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = (index: number) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    onAttachmentsChange(newAttachments);
  };

  const canAddMore = attachments.length < MAX_ATTACHMENTS && !disabled;
  const isUploading = uploading || uploadingCount > 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">
          Screenshots (optional, up to {MAX_ATTACHMENTS})
        </label>
        <span className="text-xs text-muted-foreground">
          {attachments.length}/{MAX_ATTACHMENTS}
        </span>
      </div>

      {/* Moderation error */}
      {moderationError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {moderationError.reason}
          </AlertDescription>
        </Alert>
      )}

      {/* Upload error */}
      {error && !moderationError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Attachment previews */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {attachments.map((url, index) => (
            <div
              key={url}
              className="relative group w-20 h-20 rounded-md overflow-hidden border bg-muted"
            >
              <img
                src={url}
                alt={`Attachment ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="absolute top-1 right-1 p-1 bg-background/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      {canAddMore && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled || isUploading}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isUploading}
            className="w-full"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <ImagePlus className="h-4 w-4 mr-2" />
                Add Screenshot
              </>
            )}
          </Button>
        </>
      )}
    </div>
  );
}
